import React from 'react';
import { Card, Skeleton, Empty } from 'antd';
import { useRequest } from 'ahooks';
import { getCtaRank } from '../../services/dashboardService.js';
import WordCloud from 'react-d3-cloud';

function makeSeededRandom(seed = 20251022) {
  let s = seed | 0;
  return () => {
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    return ((s >>> 0) % 100000) / 100000;
  };
}

export default function CtaWordCloudCard({ dateParams, limit = 20, height = 240 }) {
  const params = React.useMemo(() => ({ ...(dateParams || {}), limit }), [dateParams, limit]);
  const { data, loading } = useRequest(() => getCtaRank(params), {
    refreshDeps: [params.limit, params.start, params.end,params.companyId],
  });

const words = React.useMemo(
  () => {

    const sourceData = Array.isArray(data?.data || data) ? (data?.data || data) : [];

    return sourceData
      .map((w) => ({ 
        text: String(w.text), 
        value: Number(w.value) || 0 
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); 
  },
  [data]
);
  const ranked = React.useMemo(() => [...words].sort((a, b) => b.value - a.value), [words]);
  const random = React.useMemo(() => makeSeededRandom(), []);

  const containerRef = React.useRef(null);
  const svgWrapperRef = React.useRef(null);
  const [width, setWidth] = React.useState(600);

  /* Resize Observer */
  React.useEffect(() => {
    if (!containerRef.current) return;

    const handleResize = () => {
      const container = containerRef.current;
      if (container) {
        const w = container.clientWidth || 600;
        setWidth(Math.max(240, Math.floor(w)));
        
        updateSvgScale();
      }
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(containerRef.current);

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const updateSvgScale = React.useCallback(() => {
    const wrapper = svgWrapperRef.current;
    const container = containerRef.current;
    
    if (!wrapper || !container) return;

    const svg = wrapper.querySelector("svg");
    if (!svg) return;

    const bbox = svg.getBBox();
    const contentWidth = bbox.width || width;
    const contentHeight = bbox.height || height;

    if (contentWidth === 0 || contentHeight === 0) return;

    svg.style.transformOrigin = "center center";
    svg.style.width = "500px";
    svg.style.height = "100%";
  }, [width, height]);

  React.useEffect(() => {
    const timer = setTimeout(() => updateSvgScale(), 100);
    return () => clearTimeout(timer);
  }, [ranked, width, height, updateSvgScale]);

  const palette = React.useMemo(() => ['#1677ff', '#ab47bc', '#faad14', '#34a853', '#ff4d4f', '#8c8c8c'], []);
  const rankIndexMap = React.useMemo(() => Object.fromEntries(ranked.map((w, i) => [w.text, i])), [ranked]);
  const fill = React.useCallback(
    (word, i) => palette[(rankIndexMap[word.text] ?? i) % palette.length],
    [rankIndexMap, palette]
  );

  /* fontSize calculation */
  const fontSize = React.useMemo(() => {
    if (!ranked.length) return () => 12;
    const base = Math.min(width, height);
    const minPx = Math.max(12, Math.floor(base * 0.08));
    const maxPx = Math.max(minPx + 12, Math.floor(base * 0.15));

    const eps = 1;
    const logs = ranked.map(w => Math.log((w.value ?? 0) + eps));
    const lo = Math.min(...logs), hi = Math.max(...logs);

    return (w) => {
      const x = Math.log((w.value ?? 0) + eps);
      const t = (hi === lo) ? 0.5 : (x - lo) / (hi - lo);
      return Math.round(minPx + t * (maxPx - minPx));
    };
  }, [ranked, width, height]);

  /* Tooltip */
  const [hover, setHover] = React.useState(null);
  const onWordMouseOver = React.useCallback(
    (e, d) => setHover({ text: d.text, value: d.value, x: e.clientX, y: e.clientY }),
    []
  );
  const onWordMouseOut = React.useCallback(() => setHover(null), []);

  /* Render Word Cloud */
  const cloudEl = React.useMemo(
    () => (
      <WordCloud
        data={ranked}
        width={width}
        height={height} 
        font="sans-serif"
        fontSize={fontSize}
        rotate={0}
        spiral="rectangular"
        padding={1}
        fill={fill}
        random={random}
        onWordMouseOver={onWordMouseOver}
        onWordMouseOut={onWordMouseOut}
      />
    ),
    [ranked, width, height, fontSize, fill, random, onWordMouseOver, onWordMouseOut]
  );

  return (
    <Card title="CTA Word Cloud">
      {loading ? (
        <Skeleton active />
      ) : (
        <div 
          ref={containerRef} 
          style={{ 
            height: height,
            width: '100%', 
            position: 'relative', 
            overflow: 'hidden' 
          }}
        >
          {!ranked || ranked.length === 0 ? (
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              overflow: 'hidden' 
            }}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <>
              <style>{`
                .cta-wc {
                  width: 100%;
                  height: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                .cta-wc svg {
                  max-width: 100%;
                  max-height: 100%;
                }
                .cta-wc svg text {
                  transition: transform 120ms ease-out;
                  transform-box: fill-box;
                  transform-origin: 50% 50%;
                  cursor: default;
                }
                .cta-wc svg text:hover {
                  font-weight: 700;
                  filter: drop-shadow(0 1px 4px rgba(0,0,0,.25));
                }
              `}</style>

              <div className="cta-wc" ref={svgWrapperRef}>
                {cloudEl}
              </div>

              {hover && (
                <div
                  style={{
                    position: 'fixed',
                    left: hover.x + 12,
                    top: hover.y + 12,
                    background: '#e6f4ff',
                    border: '1px solid #91caff',
                    color: '#1f1f1f',
                    padding: '6px 10px',
                    borderRadius: 10,
                    fontSize: 14,
                    lineHeight: 1.2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    pointerEvents: 'none',
                    zIndex: 10000,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {`${hover.text}: ${hover.value}`}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}
